/**
 * The tables in this fork whose primary key is not a column called `id`.
 *
 * WHY THIS MAP EXISTS
 * -------------------
 * ra-data-postgrest defaults every resource to `id`. None of the tables below
 * has that column, so without an entry here a read asks PostgREST for
 * `?id=eq.5` against a table that has no such column -- a 400 on page load --
 * and an update targets no row at all.
 *
 * Declaring the real key also mints the virtual `id` react-admin requires on
 * every record, and strips it again before writing.
 *
 * WHY IT IS ITS OWN FILE
 * ----------------------
 * It began as LIFECYCLE_PRIMARY_KEYS in ./lifecycleRules.ts, which was right
 * while it held only the two lifecycle tables. `company_profile` (069) is a
 * company-page table with nothing to do with a contact's lifecycle, and a
 * constant named for the lifecycle holding it would send the next person
 * looking for it in the wrong file -- or, worse, invite a SECOND primaryKeys
 * map to be passed somewhere else, which is a config the provider silently
 * ignores because only one can win.
 *
 * One map, one injection into upstream's dataProvider.ts, guarded by
 * check-injections.mjs. Adding a keyless table is one line here.
 */
export const NON_ID_PRIMARY_KEYS = new Map<string, string[]>([
  /*
   * The primary key IS the foreign key: one lifecycle row per contact, no
   * surrogate, and therefore no way to end up with two.
   */
  ["contact_lifecycle", ["contact_id"]],

  /*
   * Keyed on the PAIR. `new` and `contacted` exist in both tracks with
   * different cadences, so a stage on its own identifies nothing.
   */
  ["lifecycle_stages", ["stage", "track"]],

  /*
   * PCM's own identifier IS the key (086). A surrogate would be a second name
   * for a thing that already has one, and creatives.pcm_design_id points at
   * theirs.
   */
  ["pcm_designs", ["design_id"]],

  /*
   * Keyed on the track itself (084). A text primary key, and no surrogate:
   * lifecycle_stages, contact_lifecycle and sequences all point at this
   * string, so a separate id would be a second name for the same thing.
   */
  ["lifecycle_tracks", ["track"]],

  /*
   * Same shape as contact_lifecycle, and for the same reason: one profile row
   * per company, keyed on company_id, no surrogate id. See migration 069 and
   * ./companyProfile.ts.
   */
  ["company_profile", ["company_id"]],
]);
